'use client'
import { useState, useEffect } from "react";
import { ElementType } from "./schema";
import supabase from '@/lib/supabase';
import useAuthStore from '@/store/useAuthStore';
import useRewardStore from '@/store/useRewardStore';
import { toastService } from "@/components/Toast/ToastManager";
import { getLocalDateString } from '@/lib/utils';
import { logGemTransaction, GemTransactionReason } from '@/lib/gem-logger';
import { HEART_LIMITS } from '@/constant/subscription';
import { getCurrentTier } from '@/store/useSubscriptionStore';

const MAX_HEART = 10; // 免费用户默认上限，保留供非订阅上下文使用

/** 根据当前用户订阅 tier 返回红心上限 */
export const getMaxHeart = (): number => {
  const tier = getCurrentTier();
  return HEART_LIMITS[tier] ?? MAX_HEART;
};
const DEFAULT_DIAMOND = 50;

const DIAMOND_EARN_RULES_ELEMENT: Partial<Record<ElementType, number>> = {
  [ElementType.INTERACTIVE]: 0,
  [ElementType.TIMELINE]: 0,
  [ElementType.DRAG_AND_DROP]: 0,
  [ElementType.WORD_SEARCH]: 0,
  [ElementType.FLASH_CARD]: 2,
  [ElementType.LESSON_SUMMARY]: 0,
  [ElementType.RECOMMEND_VIDEOS]: 0,
  [ElementType.RECOMMEND_SHORTS]: 0,
  [ElementType.SPELLING_DICTATION]: 2,
  [ElementType.QUESTION]: 2,
  [ElementType.QUESTION_V2]: 2,
  [ElementType.HIGHLIGHT]: 2,
  [ElementType.GRAMMAR_FILL_IN_BLANK]: 2,
  [ElementType.GRAMMAR_DRAG_AND_DROP]: 2,
  [ElementType.EXAMPLE_QUESTION]: 2,
};

const DIAMOND_EARN_RULES_ADDITIONAL: Partial<Record<ElementType, number>> = {
  [ElementType.RECOMMEND_VIDEOS]: 0,
  [ElementType.RECOMMEND_SHORTS]: 0,
  [ElementType.SPELLING_DICTATION]: 1,
};

const DIAMOND_COST_RULES = {
  BUY_HEART_ONE: 6,
  BUY_HEART_FILL: 50,
  BUY_GAME_TIME: 30,
};

const HEART_RECOVERY_INTERVAL = 30 * 60 * 1000; // 毫秒数
const SYNC_INTERVAL = 10 * 60 * 1000; // 10分钟

// 每日游戏时间限制 (毫秒)
export const DAILY_GAME_LIMITS = {
  WEEKDAY: 45 * 60 * 1000,  // 周中 45 分钟
  WEEKEND: 90 * 60 * 1000,  // 周末 90 分钟
};

export interface Reward {
  diamond: number;
  heart: number;
  total_heart: number;
  game_time_remaining: number; // 宝石购买的剩余游戏时间 (毫秒)
  last_heart_recovery: number; // 上次恢复红心的时间戳
}

const createDefaultReward = (): Reward => ({
  diamond: DEFAULT_DIAMOND,
  heart: MAX_HEART,
  total_heart: MAX_HEART,
  game_time_remaining: 0,
  last_heart_recovery: Date.now(),
});

const defaultReward = createDefaultReward();
let guestRewardState: Reward = createDefaultReward();
let demoRewardState: Reward = createDefaultReward();

const isDemoRewardModeActive = (): boolean => useRewardStore.getState().demoRewardMode;

const getGuestReward = (): Reward => {
  return { ...guestRewardState };
};

const setGuestReward = (reward: Reward): void => {
  if (typeof window === 'undefined') {
    return;
  }

  guestRewardState = { ...reward };
  window.dispatchEvent(new Event('rewardUpdate'));
};

const getDemoReward = (): Reward => {
  return { ...demoRewardState };
};

const setDemoReward = (reward: Reward): void => {
  demoRewardState = { ...reward };
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('rewardUpdate'));
  }
};

export const resetDemoReward = (): void => {
  demoRewardState = createDefaultReward();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('rewardUpdate'));
  }
};

// ========== 每日游戏时间限制相关函数 ==========

/**
 * 判断今天是否是周末
 */
export const isWeekend = (): boolean => {
  const day = new Date().getDay();
  return day === 0 || day === 6; // 周日=0, 周六=6
};

/**
 * 获取今日的游戏时间限制 (毫秒)
 */
export const getDailyLimit = (): number => {
  return isWeekend() ? DAILY_GAME_LIMITS.WEEKEND : DAILY_GAME_LIMITS.WEEKDAY;
};

export interface DailyGameTimeStatus {
  usedMs: number;           // 今日已用 (毫秒)
  limitMs: number;          // 今日限制 (毫秒)
  remainingMs: number;      // 今日剩余 (毫秒)
  isLimitReached: boolean;  // 是否达到限制
  localDate: string;        // 当前本地日期
  isWeekend: boolean;       // 是否周末
}

/**
 * 获取每日游戏时间状态
 * - 从 user_daily_game_time 表读取今日已用时间
 */
export const getDailyGameTimeStatus = async (): Promise<DailyGameTimeStatus> => {
  const user = useAuthStore.getState().user;
  const currentDate = getLocalDateString();
  const weekend = isWeekend();
  const limitMs = getDailyLimit();

  let usedMs = 0;

  if (user) {
    // 从 user_daily_game_time 表读取今日数据
    const { data, error } = await supabase
      .from('user_daily_game_time')
      .select('duration_seconds')
      .eq('user_id', user.id)
      .eq('date', currentDate)
      .single();

    if (!error && data) {
      usedMs = data.duration_seconds * 1000; // 转换为毫秒
    }
  }

  const remainingMs = Math.max(0, limitMs - usedMs);

  return {
    usedMs,
    limitMs,
    remainingMs,
    isLimitReached: remainingMs <= 0,
    localDate: currentDate,
    isWeekend: weekend,
  };
};

// ========== 数据库操作函数 ==========

const getDbReward = async (): Promise<Reward> => {
  if (isDemoRewardModeActive()) {
    return getDemoReward();
  }
  const user = useAuthStore.getState().user;
  if (!user) {
    return getGuestReward();
  }
  const { data, error } = await supabase
    .from('profiles')
    .select('reward')
    .eq('id', user.id)
    .single();

  //网络等其他错误处理
  if (error || !data) {
    throw new Error('Error getting reward:', error);
  }
  //有Profile，但没有reward，则初始化
  if (!data.reward || Object.keys(data.reward).length === 0) {
    const { data, error } = await supabase
      .from('profiles')
      .update({ reward: defaultReward })
      .eq('id', user.id);

    if (error) {
      throw new Error('Error updating reward:', error);
    }
    return defaultReward;
  }
  return data.reward;
}

const checkDbHeart = (heartToAdd: number, rewardHeart: number, maxHeart: number = getMaxHeart()): number => {
  if (heartToAdd < -1 || heartToAdd > maxHeart) {
    throw new Error('Invalid addDiamond or addHeart: ' + heartToAdd + ' rewardHeart: ' + rewardHeart);
  }
  if (heartToAdd + rewardHeart > maxHeart) {
    return maxHeart;
  } else if (heartToAdd + rewardHeart < 0) {
    return 0;
  } else {
    return heartToAdd + rewardHeart;
  }
}

const checkDbDiamond = (diamondToAdd: number, rewardDiamond: number): number => {
  if (rewardDiamond + diamondToAdd < 0) {
    return 0;
  }
  return diamondToAdd + rewardDiamond;
}
/**
 * 
 * @param diamondToAdd 传增量，有效范围 -100 ~ +100
 * @param heartToAdd 传增量，有效范围 -1 ~ +10
 * @returns 
 */
const updateDbReward = async (rewardToAdd: any): Promise<void> => {
  if (isDemoRewardModeActive()) {
    const { diamondToAdd, heartToAdd, last_heart_recovery, game_time_remaining } = rewardToAdd;
    const reward = getDemoReward();
    diamondToAdd && (reward.diamond = checkDbDiamond(diamondToAdd, reward.diamond));
    heartToAdd && (reward.heart = checkDbHeart(heartToAdd, reward.heart));
    last_heart_recovery && (reward.last_heart_recovery = last_heart_recovery);
    game_time_remaining !== undefined && (reward.game_time_remaining = game_time_remaining);
    setDemoReward(reward);
    return;
  }
  const user = useAuthStore.getState().user;
  if (!user) {
    const { diamondToAdd, heartToAdd, last_heart_recovery, game_time_remaining } = rewardToAdd;
    const reward = getGuestReward();
    diamondToAdd && (reward.diamond = checkDbDiamond(diamondToAdd, reward.diamond));
    heartToAdd && (reward.heart = checkDbHeart(heartToAdd, reward.heart));
    last_heart_recovery && (reward.last_heart_recovery = last_heart_recovery);
    game_time_remaining !== undefined && (reward.game_time_remaining = game_time_remaining);
    setGuestReward(reward);
    return;
  }
  const { diamondToAdd, heartToAdd, last_heart_recovery, game_time_remaining, reason, metadata } = rewardToAdd;
  const reward = await getDbReward();
  const balanceBefore = reward.diamond;

  if (diamondToAdd && diamondToAdd < 0 && reward.diamond + diamondToAdd < 0) {
    throw new Error('Insufficient gems');
  }

  diamondToAdd && (reward.diamond = checkDbDiamond(diamondToAdd, reward.diamond));
  heartToAdd && (reward.heart = checkDbHeart(heartToAdd, reward.heart));
  last_heart_recovery && (reward.last_heart_recovery = last_heart_recovery);
  game_time_remaining !== undefined && (reward.game_time_remaining = game_time_remaining);
  const { error } = await supabase
    .from('profiles')
    .update({ reward })
    .eq('id', user.id);

  if (error) {
    throw new Error('Error updating reward:', error);
  }
  setLocalReward(reward);

  if (diamondToAdd && reason) {
    logGemTransaction({
      userId: user.id,
      type: diamondToAdd > 0 ? 'earn' : 'spend',
      amount: Math.abs(diamondToAdd),
      reason: reason as GemTransactionReason,
      balanceBefore,
      balanceAfter: reward.diamond,
      metadata,
    });
  }
}

const setLocalReward = (reward: Reward): void => {
  if (isDemoRewardModeActive()) {
    setDemoReward(reward);
    return;
  }
  const user = useAuthStore.getState().user;
  if (!user) {
    setGuestReward(reward);
    return;
  }
  const now = Date.now();
  // 1. 先更新本地缓存
  localStorage.setItem(user.id + "_reward", JSON.stringify(reward));
  localStorage.setItem(user.id + "_reward_last_sync", now.toString());
  window.dispatchEvent(new Event('rewardUpdate'));
}

// 添加 useReward hook
export function useReward() {
  const user = useAuthStore(state => state.user);
  const demoRewardMode = useRewardStore(state => state.demoRewardMode);
  // 从localStorage获取初始值
  const initialReward = typeof window !== 'undefined' ?
    (demoRewardMode
      ? getDemoReward()
      : user
      ? JSON.parse(localStorage.getItem(user.id + "_reward") || JSON.stringify(defaultReward))
      : getGuestReward()) :
    defaultReward;
  const [reward, setRewardState] = useState<Reward>(initialReward);

  useEffect(() => {
    const fetchReward = async () => {
      const rewardData = await getReward();
      setRewardState(rewardData);
    };

    fetchReward();

    const updateReward = () => {
      fetchReward();
    };

    window.addEventListener('rewardUpdate', updateReward);
    return () => {
      window.removeEventListener('rewardUpdate', updateReward);
    };
  }, [demoRewardMode, user]);

  return reward;
}

/**
 * 订阅变更时同步红心上限：
 * - total_heart 更新为当前 tier 的上限
 * - 如果之前红心是满的（heart >= old total_heart），自动补满到新上限
 * - 如果之前红心不满，保持当前值不变（但上限提高）
 */
const syncHeartLimitIfNeeded = (reward: Reward): boolean => {
  const maxHeart = getMaxHeart();
  if (reward.total_heart === maxHeart) return false;

  const wasFull = reward.heart >= reward.total_heart;
  reward.total_heart = maxHeart;
  if (wasFull) {
    reward.heart = maxHeart;
  }
  return true;
};

// 检查并恢复红心
export const checkAndRecoverHeart = (reward: Reward): void => {
  const maxHeart = getMaxHeart();
  if (reward.heart >= maxHeart) {
    return;
  }

  const now = Date.now();
  const timePassed = now - reward.last_heart_recovery;
  const heartsToAdd = Math.min(Math.floor(timePassed / HEART_RECOVERY_INTERVAL), maxHeart - reward.heart);

  if (heartsToAdd > 0) {
    updateDbReward({
      heartToAdd: heartsToAdd,
      last_heart_recovery: now - (timePassed % HEART_RECOVERY_INTERVAL),
    });
  }
};

export const costHeart = async (): Promise<boolean> => {
  const reward = await getReward();
  const maxHeart = getMaxHeart();

  if (reward.heart > 0) {
    let last_heart_recovery = reward.last_heart_recovery;
    if (reward.heart >= maxHeart) {
      last_heart_recovery = Date.now();
    }
    reward.heart -= 1;
    await updateDbReward({
      heartToAdd: -1,
      last_heart_recovery: last_heart_recovery,
    });
    return true;
  } else {
    return false;
  }
}

export const getRequiredDiamondWhenBuyHeart = (type: 'one' | 'full'): number => {
  if (type === 'one') {
    return DIAMOND_COST_RULES.BUY_HEART_ONE;
  }
  return DIAMOND_COST_RULES.BUY_HEART_FILL;
}

export const buyHeartOne = async (): Promise<boolean> => {
  const reward = await getReward();
  const maxHeart = getMaxHeart();
  if (reward.diamond >= DIAMOND_COST_RULES.BUY_HEART_ONE && reward.heart < maxHeart) {
    try {
      await updateDbReward({
        diamondToAdd: -DIAMOND_COST_RULES.BUY_HEART_ONE,
        heartToAdd: 1,
        reason: GemTransactionReason.BUY_HEART_ONE,
      });
      return true;
    } catch {
      toastService.error('Not enough gems');
      return false;
    }
  }
  toastService.error('Not enough gems');
  return false;
}

export const buyHeartFill = async (): Promise<boolean> => {
  const reward = await getReward();
  const maxHeart = getMaxHeart();
  const heartCount = reward.heart;
  if (reward.diamond >= DIAMOND_COST_RULES.BUY_HEART_FILL && reward.heart < maxHeart) {
    try {
      await updateDbReward({
        diamondToAdd: -(maxHeart - heartCount) * DIAMOND_COST_RULES.BUY_HEART_ONE,
        heartToAdd: maxHeart - reward.heart,
        reason: GemTransactionReason.BUY_HEART_FILL,
      });
      return true;
    } catch {
      toastService.error('Not enough gems');
      return false;
    }
  }
  toastService.error('Not enough gems');
  return false;
}

export const addDiamondByElementType = async (elementType: ElementType, challenge?: boolean, multiplier: number = 1): Promise<boolean> => {
  await updateDbReward({
    diamondToAdd: (challenge ? 3 : DIAMOND_EARN_RULES_ELEMENT[elementType] || 0) * multiplier,
    reason: GemTransactionReason.LEARN_COMPLETE,
  });
  return true;
}

export const addAdditionalDiamondByElementType = async (elementType: ElementType): Promise<boolean> => {
  await updateDbReward({
    diamondToAdd: DIAMOND_EARN_RULES_ADDITIONAL[elementType] || 0,
    reason: GemTransactionReason.LEARN_COMPLETE,
  });
  return true;
}

export const getTotalDiamondByElementType = (elementType: ElementType | null, challenge?: boolean): number => {
  if (!elementType) return 0;
  return (challenge ? 3 : DIAMOND_EARN_RULES_ELEMENT[elementType as ElementType] ?? 0);
}

export const getAdditionalDiamondByElementType = (elementType: ElementType | null): number => {
  if (!elementType) return 0;
  return DIAMOND_EARN_RULES_ADDITIONAL[elementType] ?? 0;
}

const getReward = async (): Promise<Reward> => {
  try {
    const user = useAuthStore.getState().user;
    if (!user) {
      return getGuestReward();
    }

    // 1. 先尝试从本地读取
    const localReward = localStorage.getItem(user.id + "_reward");
    const lastSync = Number(localStorage.getItem(user.id + "_reward_last_sync") || 0);
    const now = Date.now();

    if (localReward && now - lastSync < SYNC_INTERVAL) {
      const rewardData = {
        ...defaultReward,  // 先使用默认值
        ...JSON.parse(localReward) as Reward,    // 再用实际数据覆盖
      } as Reward;

      // 订阅变更时同步红心上限（写回 DB + 本地缓存）
      if (syncHeartLimitIfNeeded(rewardData)) {
        localStorage.setItem(user.id + "_reward", JSON.stringify(rewardData));
        supabase.from('profiles').update({ reward: rewardData }).eq('id', user.id)
          .then(({ error }) => { if (error) console.error('Error syncing heart limit:', error); });
      }

      checkAndRecoverHeart(rewardData);
      return rewardData;
    }

    // 2. 超过同步间隔，拉取服务器数据
    const dbReward = await getDbReward();

    // 确保数据包含所有必需字段
    const rewardData = {
      ...defaultReward,  // 先使用默认值
      ...dbReward,    // 再用实际数据覆盖
    } as Reward;

    // 订阅变更时同步红心上限
    if (syncHeartLimitIfNeeded(rewardData)) {
      await supabase.from('profiles').update({ reward: rewardData }).eq('id', user.id);
    }

    checkAndRecoverHeart(rewardData);
    localStorage.setItem(user.id + "_reward", JSON.stringify(rewardData));
    localStorage.setItem(user.id + "_reward_last_sync", now.toString());
    return rewardData;
  } catch (error) {
    console.error('Error getting reward:', error);
    return defaultReward;
  }
};

export const addDiamond = async (count: number, reason: GemTransactionReason = GemTransactionReason.TASK_REWARD): Promise<void> => {
  await updateDbReward({
    diamondToAdd: count,
    reason,
  })
}

export const addDebugGems = async (count: number = 100): Promise<void> => {
  if (process.env.NODE_ENV === 'development') {
    addDiamond(count, GemTransactionReason.DEBUG)
  }
};

export const setDebugGems = async (count: number): Promise<void> => {
  if (process.env.NODE_ENV === 'development') {

  }
};

export const getRequiredDiamondWhenBuyGameTime = (): number => {
  return DIAMOND_COST_RULES.BUY_GAME_TIME;
}

export const buyGameTime = async (): Promise<void> => {
  const reward = await getReward();
  const addMs = 10 * 60 * 1000; // 10分钟
  const currentRemainingMs = reward.game_time_remaining || 0; // 获取当前剩余时间
  const newTotalMs = currentRemainingMs + addMs; // 累加新购买的时间

  if (reward.diamond >= DIAMOND_COST_RULES.BUY_GAME_TIME) {
    await updateDbReward({
      diamondToAdd: -DIAMOND_COST_RULES.BUY_GAME_TIME,
      game_time_remaining: newTotalMs,
      reason: GemTransactionReason.BUY_GAME_TIME,
    })
  } else {
    throw new Error('Not enough diamond');
  }
}

// 新的函数：直接购买指定时间和宝石数量
export const buyGameTimeWithAmount = async (minutes: number, gemCost: number): Promise<void> => {
  const reward = await getReward();
  const addMs = minutes * 60 * 1000; // 转换为毫秒
  const currentRemainingMs = reward.game_time_remaining || 0; // 获取当前剩余时间
  const newTotalMs = currentRemainingMs + addMs; // 累加新购买的时间

  if (reward.diamond >= gemCost) {
    await updateDbReward({
      diamondToAdd: -gemCost,
      game_time_remaining: newTotalMs,
      reason: GemTransactionReason.BUY_GAME_TIME,
    })
  } else {
    throw new Error('Not enough diamond');
  }
}

export const getRemainingGameTime = async (): Promise<number> => {
  const reward = await getReward();
  if (reward.game_time_remaining) {
    return Math.max(0, Math.floor(reward.game_time_remaining / 1000)); // 返回剩余秒数
  }
  return 0;
}

export const startGameTime = async (): Promise<string> => {
  // 生成随机ID: 时间戳 + 6位随机数
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 1000000).toString().padStart(6, '0');
  const gameId = `${timestamp}-${random}`;

  // 记录开始时间到localStorage
  localStorage.setItem(`game_time_${gameId}`, timestamp.toString());

  return gameId;
}

export interface CountDownGameTimeResult {
  gemTimeRemaining: number;      // 宝石时间剩余 (秒)
  dailyTimeRemaining: number;    // 每日时间剩余 (秒)
  dailyLimitReached: boolean;    // 是否达到每日限制
}

export const countDownGameTime = async (gameId: string): Promise<CountDownGameTimeResult> => {
  const startTime = localStorage.getItem(`game_time_${gameId}`);
  console.log('countDownGameTime', startTime);
  if (!startTime) {
    return { gemTimeRemaining: 0, dailyTimeRemaining: 0, dailyLimitReached: false };
  }

  const user = useAuthStore.getState().user;
  const reward = await getReward();
  const now = Date.now();
  const elapsedMs = now - Number(startTime);
  const elapsedSeconds = Math.floor(elapsedMs / 1000);

  // 更新宝石时间 (现有逻辑)
  const game_time_remaining = Math.max(0, reward.game_time_remaining - elapsedMs);

  // 更新 localStorage 时间戳
  localStorage.setItem(`game_time_${gameId}`, now.toString());

  // 更新宝石时间到数据库
  updateDbReward({ game_time_remaining });

  // 更新每日时间到 user_daily_game_time 表
  const currentDate = getLocalDateString();
  const dailyLimit = getDailyLimit();
  let dailyRemainingMs = dailyLimit;

  if (user && elapsedSeconds > 0) {
    // 使用 upsert 更新每日游戏时间
    const { data: existingRecord } = await supabase
      .from('user_daily_game_time')
      .select('duration_seconds')
      .eq('user_id', user.id)
      .eq('date', currentDate)
      .single();

    const currentDurationSeconds = existingRecord?.duration_seconds || 0;
    const newDurationSeconds = currentDurationSeconds + elapsedSeconds;

    await supabase
      .from('user_daily_game_time')
      .upsert({
        user_id: user.id,
        date: currentDate,
        duration_seconds: newDurationSeconds,
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id,date',
      });

    dailyRemainingMs = Math.max(0, dailyLimit - newDurationSeconds * 1000);
  }

  return {
    gemTimeRemaining: Math.max(0, Math.floor(game_time_remaining / 1000)),
    dailyTimeRemaining: Math.floor(dailyRemainingMs / 1000),
    dailyLimitReached: dailyRemainingMs <= 0,
  };
}

export const resumeGameTimeSession = async (sessionId: string): Promise<void> => {
  // Simply update the session's start/resume timestamp in localStorage to now.
  // This prevents time deduction for the period the game was paused.
  localStorage.setItem(`game_time_${sessionId}`, Date.now().toString());
}

export const forceSetRemainingGameTime = async (seconds: number, activeGameSessionId?: string | null): Promise<void> => {
  const game_time_remaining = seconds * 1000; // Set to desired seconds in milliseconds

  // If there's an active game session, update its start/resume timestamp in localStorage
  // to prevent incorrect deduction on the next tick.
  if (activeGameSessionId) {
    localStorage.setItem(`game_time_${activeGameSessionId}`, Date.now().toString());
  }

  updateDbReward({ game_time_remaining: game_time_remaining })
}

// 花费gems购买outfit
export const buyOutfit = async (price: number): Promise<boolean> => {
  const reward = await getReward();
  if (reward.diamond < price) {
    return false;
  }
  try {
    await updateDbReward({
      diamondToAdd: -price,
      reason: GemTransactionReason.BUY_OUTFIT,
    });
    return true;
  } catch {
    return false;
  }
}

export const userReceiveGift = async (): Promise<void> => {
  await updateDbReward({
    diamondToAdd: 50,
    reason: GemTransactionReason.GIFT_CLAIM,
  })
}

export const buySpin = async (): Promise<boolean> => {
  const reward = await getReward();
  if (reward.diamond >= 30) {
    try {
      await updateDbReward({
        diamondToAdd: -30,
        reason: GemTransactionReason.BUY_SPIN,
      });
      return true;
    } catch {
      return false;
    }
  }
  return false;
}
